import { useEffect, useRef, useState } from 'react'

import { GetServerSideProps } from 'next'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { sendGTMEvent } from '@next/third-parties/google'

import { atmLogo } from '@ui/assets'
import { Seo } from '@ui/components/seo'
import { Spinner } from '@ui/components/Spinner'
import { appUrl, canonicalMarketingUrl } from '@ui/lib/urls'

import { useEventTracking } from '@website/hooks/useEventTracking'
import {
  parseUtmCookie,
  replacePlaceholdersWithUtmData,
  UTM_PARAMS,
  UtmData,
} from '@website/utils/utm'

interface GoPageProps {
  initialUtmData?: UtmData
}

const trackingLinks: Record<string, Record<string, string>> = {
  '/best-budgeting-apps': {
    atm: `${appUrl('/personal-finance')}?utm_source=atm&utm_medium=content&utm_campaign=best-budgeting-apps&utm_term={utm_term}&utm_content={utm_content}&utm_id={utm_id}`,
    klover:
      'https://klover.pxf.io/OrmAvn?subId2=best-budgeting-apps&subId3=atm.com',
    monarchmoney:
      'https://monarchmoney.sjv.io/0J5oKE?subId2=best-budgeting-apps&subId3=atm.com',
    hiatus:
      'https://hiatus.pxf.io/9WBazy?subId2=best-budgeting-apps&subId3=atm.com',
    rocketmoney:
      'https://onboarding.rocketmoney.com/?utm_campaign=atm.com&utm_medium=referral&utm_source=atm.com',
    ynab: 'https://www.ynab.com/?utm_campaign=atm.com&utm_medium=referral&utm_source=atm.com',
    pocketguard:
      'https://pocketguard.com/?utm_campaign=atm.com&utm_medium=referral&utm_source=atm.com',
  },
  '/best-cash-advance-apps': {
    atm: `${appUrl('/')}?utm_source=atm&utm_medium=content&utm_campaign=best-cash-advance-apps&utm_term={utm_term}&utm_content={utm_content}&utm_id={utm_id}`,
    brigit:
      'https://brigit.pxf.io/JrZQ0R?subId2=best-cash-advance-apps&subId3=atm.com',
    current:
      'https://current.pxf.io/QOjJ1x?subId2=best-cash-advance-apps&subId3=atm.com',
    klover:
      'https://klover.pxf.io/OrmAvn?subId2=best-cash-advance-apps&subId3=atm.com',
    sorbet:
      'https://shifttime.sjv.io/EKo5en?subId2=best-cash-advance-apps&subId3=atm.com',
  },
  '/compare/bank-account': {
    chime:
      'https://chime.pxf.io/c/2189953/2455355/28165?subId2=best-cash-advance-apps&subId3=atm.com',
    walmartmoneycard:
      'https://walmart-money-card.pxf.io/c/2189953/1095992/12970?subId2=compare-bank-account&subId3=atm.com',
    mph: 'https://www.dpbolvw.net/click-100374569-15388558?sid=compare-bank-account',
  },
  '/compare/budgeting': {
    atm: `${appUrl('/')}?utm_source=atm&utm_medium=content&utm_campaign=compare-budgeting&utm_term={utm_term}&utm_content={utm_content}&utm_id={utm_id}`,
    klover:
      'https://klover.pxf.io/OrmAvn?subId2=compare-budgeting&subId3=atm.com',
    monarchmoney:
      'https://monarchmoney.sjv.io/0J5oKE?subId2=compare-budgeting&subId3=atm.com',
    hiatus:
      'https://hiatus.pxf.io/9WBazy?subId2=compare-budgeting&subId3=atm.com',
  },
  '/compare/cash-advance': {
    atm: `${appUrl('/')}?utm_source=atm&utm_medium=content&utm_campaign=compare-cash-advance&utm_term={utm_term}&utm_content={utm_content}&utm_id={utm_id}`,
    brigit:
      'https://brigit.pxf.io/JrZQ0R?subId2=compare-cash-advance&subId3=atm.com',
    current:
      'https://current.pxf.io/QOjJ1x?subId2=compare-cash-advance&subId3=atm.com',
    klover:
      'https://klover.pxf.io/OrmAvn?subId2=compare-cash-advance&subId3=atm.com',
    wisely: 'https://www.mywisely.com/',
  },
  '/compare/credit-builder-account': {
    self: 'https://self-lender.3qcw.net/VyjzMa?subId2=compare-credit-builder-account&subId3=atm.com',
    'creditstrong-instal':
      'https://austin-capital-bank.sjv.io/e14JPr?subId2=compare-credit-builder-account&subId3=atm.com',
    'creditstrong-magnum':
      'https://austin-capital-bank.sjv.io/e14JPr?subId2=compare-credit-builder-account&subId3=atm.com',
    'creditstrong-revolv':
      'https://austin-capital-bank.sjv.io/e14JPr?subId2=compare-credit-builder-account&subId3=atm.com',
  },
  '/compare/investing': {
    acorns:
      'https://acorns.sjv.io/c/2189953/1544868/5136?subId2=compare-investing&subId3=atm.com',
    stash:
      'https://stash.zytd7d.net/c/2189953/2139095/4799?subId2=compare-investing&subId3=atm.com',
    moomoo:
      'https://moomootechnologiesinc.pxf.io/KjVNry?subId2=compare-investing&subId3=atm.com',
  },
  '/make-money-paid-surveys': {
    atm: 'https://atm.app.link/3scsZPSBIIb',
    swagbucks: 'https://swagbucks.7eer.net/y217n2',
    mypoints: 'https://mypoints.sjv.io/LXNQzZ',
    inboxdollars: 'https://inboxdollars.sjv.io/QyQ5RA',
    ysense: 'https://ysense.sjv.io/jrLvM5',
  },
}

const title = 'Redirecting you to our partner site, please wait...'
const description = 'If you are not redirected automatically, click here.'
const canonical = canonicalMarketingUrl('/compare')

const decodeQueryParam = (param: string | string[] | undefined): string => {
  try {
    if (!param) return ''
    const value = Array.isArray(param) ? param[0] : param
    return decodeURIComponent(value)
  } catch (error) {
    console.error('Error decoding query parameter:', param, error)
    return ''
  }
}

const GoPage = ({ initialUtmData = {} }: GoPageProps) => {
  const router = useRouter()
  const { isReady, query } = router
  const { source, offer_id } = query
  const hasRedirected = useRef(false)
  const [link, setLink] = useState<string>('#')
  const { trackEvent } = useEventTracking()

  useEffect(() => {
    if (!isReady || hasRedirected.current) return

    if (!source || !offer_id) {
      router.replace('/404')
      return
    }

    const decodedSource = decodeQueryParam(source)
    const decodedOfferId = decodeQueryParam(offer_id)

    if (!decodedSource || !decodedOfferId) {
      router.replace('/404')
      return
    }

    let link = trackingLinks[decodedSource]?.[decodedOfferId]

    if (link) {
      link = replacePlaceholdersWithUtmData(link, initialUtmData)
      setLink(link)
      hasRedirected.current = true
      return trackAndRedirect(link)
    } else {
      router.replace('/404')
    }
  }, [isReady, source, offer_id, initialUtmData])

  const trackAndRedirect = (url: string) => {
    let hasRedirected = false

    const redirect = () => {
      if (hasRedirected) return
      hasRedirected = true
      window.location.href = url
    }

    const sendGTMEventPromise = new Promise<void>((resolve) => {
      sendGTMEvent({
        event: 'conversion_click',
        event_category: 'Outbound Link',
        event_label: url,
        value: 1,
        eventCallback: resolve,
        transport: 'beacon',
      })
    })

    const trackEventPromise = trackEvent('go_redirect', { url: url })

    const timeoutId = setTimeout(() => {
      console.warn('Timeout reached, redirecting')
      redirect()
    }, 1500)

    Promise.all([sendGTMEventPromise, trackEventPromise])
      .then(() => {
        clearTimeout(timeoutId)
        redirect()
      })
      .catch((error) => {
        console.error('Error in tracking and redirecting:', error)
        clearTimeout(timeoutId)
        redirect()
      })
  }

  return (
    <>
      <Seo title={title} description={description} canonical={canonical} />
      <div className="flex h-screen">
        <div className="mx-auto flex flex-col items-center justify-center gap-2 p-8">
          <Image src={atmLogo} alt="ATM.com" className="h-16 w-auto" />
          <div className="my-4 flex flex-col items-center gap-2 text-center">
            <p
              className="text-lg font-semibold text-gray-700"
              role="status"
              aria-live="polite"
            >
              Redirecting you to our partner site, please wait...
            </p>
            <p className="text-xs text-gray-500 sm:text-sm">
              If you are not redirected automatically,{' '}
              <a href={link} className="text-blueberry-700 underline">
                click here
              </a>
              .
            </p>
          </div>
          <Spinner className="text-blueberry-700 h-12 w-12" />
        </div>
      </div>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  req,
  query,
}) => {
  const fromCookie = parseUtmCookie(req.cookies['utm_params'])
  const fromQuery: UtmData = {}
  UTM_PARAMS.forEach((param) => {
    const value = query[param]
    if (typeof value === 'string') fromQuery[param] = value
  })

  return {
    props: {
      initialUtmData: { ...fromCookie, ...fromQuery },
    },
  }
}

export default GoPage
